import React from 'react';
import type { DietPlan } from '../../api/dietApi';
import { downloadDietPdf } from '../../api/dietApi';

interface DietPlanWidgetProps {
  plan: DietPlan | null;
}

/**
 * DietPlanWidget — блок послуг дієтолога / стану раціону.
 * Три стани відповідно до прототипу prototyp-panel-pacjenta_8.html:
 *
 * A (none) — «Usługi dietetyka»: дашована рамка, badge «Nowość», chevron → /dietetyk-online/
 * B (in_progress) — «Twój plan żywieniowy «W toku»»: фон #F1ECFB, badge «W toku», без кнопок
 * C (done) — «Twój plan żywieniowy «Gotowy»»: фон #361777, 2 кнопки (Otwórz plan + Kolejny miesiąc)
 */
export const DietPlanWidget: React.FC<DietPlanWidgetProps> = ({ plan }) => {
  const STYLES = `
    /* ── DietPlanWidget ── */

    /* Стан A: пунктирна рамка — «Usługi dietetyka» */
    .dpw-none {
      display: block; width: 100%; text-align: left; cursor: pointer;
      border-radius: 24px;
      padding: 16px 20px; margin-bottom: 12px;
      background: #FAF8FE;
      box-shadow: none !important;
      border: none;
      transition: background 0.2s;
    }
    .dpw-none:hover { background: #F4F0FA; }
    .dpw-none:active { background: #F1ECFB; }

    /* Стан B: бузковий — «W toku» */
    .dpw-wait {
      background: #F1ECFB; border: 1px solid #D6C1F1; border-radius: 24px;
      padding: 16px; margin-bottom: 12px;
    }

    /* Стан C: темний — «Gotowy» */
    .dpw-done {
      background: #361777; border-radius: 24px; padding: 16px; margin-bottom: 12px;
    }

    /* Загальні елементи */
    .dpw-rowc { display: flex; align-items: center; gap: 16px; width: 100%; }
    .dpw-iconc {
      width: 48px; height: 48px; flex: none; 
      background: #fff !important;
      border-radius: 100px;
      display: flex; align-items: center; justify-content: center;
    }
    .dpw-rowtxt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .dpw-rowtxt b {
      font-size: 16px; font-weight: 600; line-height: 140%; color: #361777;
      display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    }
    .dpw-rowtxt span { font-size: 12px; font-weight: 500; line-height: 140%; color: #786798; }
    .dpw-chev { flex: none; display: flex; align-items: center; }

    /* Badge «Nowość» (стан A) */
    .dpw-badge-new {
      display: inline-flex; align-items: center; padding: 3px 10px;
      border-radius: 100px; background: #D6C1F1; color: #fff;
      font-size: 11px; font-weight: 500; line-height: 140%; white-space: nowrap;
    }

    /* Planhead (стани B і C) */
    .dpw-planhead {
      display: flex; align-items: center; justify-content: space-between; gap: 10px;
    }
    .dpw-planhead b { font-size: 16px; font-weight: 600; line-height: 140%; white-space: nowrap; }
    .dpw-planhead b.dpw-white { color: #fff; }
    .dpw-plansub { font-size: 12px; font-weight: 500; line-height: 140%; margin-top: 4px; }
    .dpw-plansub--wait { color: #57437E; }
    .dpw-plansub--done { color: rgba(255,255,255,.72); }

    /* Badge «W toku» (стан B) */
    .dpw-badge-wait {
      display: inline-flex; align-items: center; padding: 3px 10px;
      border-radius: 100px; background: #D6C1F1; color: #fff;
      font-size: 11px; font-weight: 500; white-space: nowrap; flex: none;
    }

    /* Badge «Gotowy» (стан C) */
    .dpw-badge-ok {
      display: inline-flex; align-items: center; padding: 3px 10px;
      border-radius: 100px; background: #DFF3E8; color: #1B6B45;
      font-size: 11px; font-weight: 500; white-space: nowrap; flex: none;
    }

    /* Кнопки (стан C) */
    .dpw-btnrow { display: flex; gap: 8px; margin-top: 14px; }
    .dpw-btnrow button, .dpw-btnrow a {
      flex: 1; height: 44px; border-radius: 100px; font-size: 14px; font-weight: 500;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      font-family: inherit; text-decoration: none; transition: opacity .15s;
    }
    .dpw-btnrow button:active, .dpw-btnrow a:active { opacity: .85; }
    .dpw-btn-white { background: #fff; color: #361777; border: none; }
    .dpw-btn-ghostw { background: none; border: 1px solid rgba(255,255,255,.4); color: #fff; }

    /* Кнопки Meet (стан B з підтвердженим слотом) */
    .dpw-btn-meet-active {
      display: flex; align-items: center; justify-content: center; width: 100%;
      height: 44px; border-radius: 100px; background: #361777; color: #fff; border: none;
      font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit;
      text-decoration: none; transition: opacity .15s;
    }
    .dpw-btn-meet-active:active { opacity: .85; }
    .dpw-btn-meet-inactive {
      display: flex; align-items: center; justify-content: center; width: 100%;
      height: 44px; border-radius: 100px; background: #D6C1F1; color: #786798; border: none;
      font-size: 14px; font-weight: 500; cursor: default; font-family: inherit; opacity: .7;
    }

    @media (min-width: 1024px) {
      .dpw-none {
        background: #FFFFFF;
        box-shadow: 0 4px 24px rgba(54, 23, 119, 0.05) !important;
        padding: 16px;
      }
      .dpw-iconc {
        width: 80px; height: 80px;
        background: #FAF8FE !important;
        border-radius: 20px;
      }
    }
  `;

  const handleOpenPlan = () => {
    const pdfUrl = plan?.pdf_url;
    // Використовуємо pdf_order_id, якщо він є
    const orderId = (plan as any)?.pdf_order_id || plan?.order_id;

    if (pdfUrl) {
      downloadDietPdf(pdfUrl, `plan-zywieniowy-${orderId || 'diet'}.pdf`, true);
    } else {
      console.error('No pdfUrl found in the plan object', plan);
      alert('Nie znaleziono pliku PDF (Jadłospisu) dla tego zamówienia ani poprzednich.');
    }
  };

  const hasMeet = !!(plan?.meet_link && plan?.slot_timestamp);
  const [isMeetActive, setIsMeetActive] = React.useState(false);

  React.useEffect(() => {
    if (!hasMeet) return;
    const checkActive = () => {
      // 24 hours = 24 * 60 * 60 = 86400 seconds
      const active = Math.floor(Date.now() / 1000) >= ((plan?.slot_timestamp || 0) - 24 * 60 * 60);
      setIsMeetActive(active);
    };
    checkActive();
    const interval = setInterval(checkActive, 60000);
    return () => clearInterval(interval);
  }, [hasMeet, plan?.slot_timestamp]);

  if (plan?.status !== 'done' && plan?.status !== 'in_progress') {
    return null;
  }

  return (
    <>
      <style>{STYLES}</style>

      {plan?.status === 'done' && (
        <div className="dpw-done">
          <div className="dpw-planhead">
            <b className="dpw-white">Twój plan żywieniowy</b>
            <span className="dpw-badge-ok">Gotowy</span>
          </div>
          <p className="dpw-plansub dpw-plansub--done">Jadłospis na 30 dni od dietetyka</p>
          <div className="dpw-btnrow">
            <button className="dpw-btn-white" onClick={handleOpenPlan}>
              Otwórz plan
            </button>
            <a className="dpw-btn-ghostw" href="https://stage.tabletka24.pl/diet-funnel/?product=6835">
              Kolejny miesiąc
            </a>
          </div>
        </div>
      )}

      {plan?.status === 'in_progress' && (
        <div className="dpw-wait">
          <div className="dpw-planhead">
            <b>Twój plan żywieniowy</b>
            <span className="dpw-badge-wait">W&nbsp;toku</span>
          </div>
          <p className="dpw-plansub dpw-plansub--wait">
            {hasMeet
              ? `Konsultacja: ${plan.slot}`
              : 'Dietetyk już nad nim pracuje — damy znać e-mailem, gdy będzie gotowy.'}
          </p>
          {hasMeet && (
            <div className="dpw-btnrow" style={{ marginTop: 12 }}>
              {isMeetActive ? (
                <a
                  className="dpw-btn-meet-active"
                  href={plan.meet_link!}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dołącz przez Google Meet
                </a>
              ) : (
                <button className="dpw-btn-meet-inactive" disabled>
                  Dołącz — aktywne 24h przed
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
